import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  StudentEnrollmentListDto,
  StudentEnrollmentListResource,
  StudentEnrollmentFilter as StudentEnrollmentFilterModel,
  StudentEnrollmentListFilter,
} from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { StudentEnrollmentFilter } from "../StudentEnrollmentFilter";
import { studentEnrollmentPageRoute } from "../StudentEnrollmentPage/StudentEnrollmentPage";
import { StudentEnrollmentTable } from "./StudentEnrollmentTable";
import { FilterDrawerButton } from "components/common/FilterDrawerButton";
import { FilterDrawer } from "pages/students/students/FilterDrawer";

const routeName = "studentEnrollments";

export const studentEnrollmentListPageRoute: PageRoute = {
  handle: "Enrollments",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentEnrollmentListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentEnrollment;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentEnrollmentListFilter>();

  const [initListFilter] = useState<StudentEnrollmentListFilter>({
    sortObjects: [],
    studentFilter: {},
    enrollmentFilter: {},
    customFilters: [],
    pageSize: 20
  });

  const [listItems, setListItems] = useState<StudentEnrollmentListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentEnrollmentListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const [filterOpen, setFilterOpen] = useState(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentEnrollments.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentEnrollments.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    ApiService.studentEnrollments.initListFilter().then((response) => {
      setListFilter(response);
      loadList(response);
    });

    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentEnrollments.getListResource().then((response) => {
      setResource(response);
    });
  };

  const getIdsMethods = () => ApiService.studentEnrollments.getIds(listFilter);

  const updateListFilter = (updatedFilter: StudentEnrollmentListFilter) => {
    setListFilter(updatedFilter);
    loadList(initListFilter, { resetPage: true });
  };

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentEnrollmentListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate({
                      pathname: studentEnrollmentPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "standard" }),
                    })
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />

              <FilterDrawerButton
                initListFilter={initListFilter}
                listFilter={listFilter}
                setFilterOpen={setFilterOpen}
                clearFilter={updateListFilter}
              />
            </Stack>
          }
          tableContainer={
            <StudentEnrollmentTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentEnrollments={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      {/* FILTER DRAWER */}
      <FilterDrawer
        listFilter={listFilter}
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onReset={() => {
          setListFilter(initListFilter);
          loadList(initListFilter, { resetPage: true });
        }}
        onApply={() => {
          setListFilter(
            produce(listFilter, (draft) => {
              draft.page = 1;
            })

          );

          loadList(
            produce(listFilter, (draft) => {
              draft.page = 1;
            }),
            { resetPage: true }
          );

          setFilterOpen(false);
        }}
      >
        <StudentEnrollmentFilter
          resource={resource}
          filter={listFilter || ({} as StudentEnrollmentListFilter)}
          setFilter={(filter) =>
            setListFilter(filter)
          }
        />
      </FilterDrawer>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
